/* eslint-disable @nx/enforce-module-boundaries */
'use-client';
import { SMSIcon } from '@mybridge/icons';
import { Flex, Switch, Text, useToast } from '@mybridge/ui';
import { useTranslator } from '@mybridge/ui/custom-hooks/useTranslation';
import { useMutation } from '@tanstack/react-query';
import { useState, useEffect, useMemo } from 'react';
import { AuthenticatedProviders } from 'v4/snippets/providers/authenticated';
import API from 'v4/store/common/api';
import { APIEndPoints } from 'v4/store/common/endPoint';

const SMSToggle = ({ companyPage, module, settings = [], onChange, isDisabled }) => {
  const { t } = useTranslator();
  const toast = useToast({ position: 'top', isClosable: true });
  
  // Generate a unique ID for the switch
  const switchId = useMemo(() => 
    `sms-${module}${companyPage ? `-${companyPage}` : ''}`, 
    [module, companyPage]
  );
  
  const [isEnabled, setIsEnabled] = useState(false);
  
  // Set state based on API data ONLY
  useEffect(() => {
    let enabled = false;
    
    if (companyPage) {
      // For company pages, find the specific company page setting
      const companySetting = settings.find(
        (item) =>
          item._for === module &&
          item.company_page === companyPage
      );
      enabled = companySetting ? companySetting.enabled : false;
    } else {
      // For non-company page settings, find the most recent setting without company_page
      const moduleSettings = settings.filter(
        (item) => item._for === module && !item.company_page
      );
      
      if (moduleSettings.length > 0) {
        // Sort by updated_at in descending order and get the first (most recent) one
        const sortedSettings = moduleSettings.sort((a, b) => {
          const dateA = new Date(a.updated_at || a.created_at);
          const dateB = new Date(b.updated_at || b.created_at);
          return dateB - dateA; // Descending order (newest first)
        });
        
        enabled = sortedSettings[0].enabled;
      }
    }
    
    setIsEnabled(enabled);
  }, [settings, module, companyPage]);

  const { mutateAsync, isLoading } = useMutation({
    mutationFn: async (body) => {
      return await API({
        method: 'POST',
        url: `${APIEndPoints.Notification}/sms/`,
        body: JSON.stringify(body),
      });
    },
  });

  const onChange_ = async () => {
    const newState = !isEnabled;
    
    try {
      await mutateAsync({
        _for: module,
        enabled: newState,
        ...(companyPage ? { company_page: companyPage } : {}),
      });
      
      // Refetch data to ensure consistency
      if (onChange) {
        await onChange();
      }
      
      toast({
        title: t('toast.success') || "Success",
        description: `SMS ${t('notification.notifications')||"notifications"} ${newState ? (t('notification.enabled') || "enabled") : (t('notification.disabled') || "disabled")}`,
        status: "success",
        duration: 2000,
      });
    } catch (error) {
      console.error("Error toggling SMS notification:", error);
      
      toast({
        title: "Error",
        description: "Failed to update notification settings",
        status: "error",
        duration: 3000,
      });
    }
  };

  return (
    <AuthenticatedProviders>
      <Flex alignItems="center" justifyContent="space-between" pt="15px">
        <Flex>
          <SMSIcon color="#68A4D1" />
          <Text ms="10px" color="brandGray.850">{"SMS"}</Text>
        </Flex>
        <Switch
          name={switchId}
          label="SMS"
          value={isEnabled}
          isChecked={isEnabled}
          onChange={onChange_}
          isDisabled={isLoading || isDisabled}
        />
      </Flex>
    </AuthenticatedProviders>
  );
};

export default SMSToggle;
